import React, { useState, useRef } from "react";
import { isValidDocument } from "../helpers/validateDocumentHelpers";

export const useFormValidation = (setUserInfo: any) => {
  const numeroRef = useRef<HTMLInputElement | null>(null);

  const [isInvoiceDataFilled, setIsInvoiceDataFilled] =
    useState<boolean>(false);

  // Estado que guarda quais campos estão com erro
  const [errosInput, setErrosInput] = useState<Record<string, boolean>>({});

  // Estado que guarda quais campos o usuário já interagiu
  const [fieldsTouched, setFieldsTouched] = useState<Record<string, boolean>>(
    {},
  );

  const formatDocumentNumber = (value: any) => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length <= 11) {
      // CPF: 000.000.000-00
      return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      // CNPJ: 00.000.000/0000-00
      return cleanValue.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    }
  };

  //Máscara do CEP: 00000-000
  const formatCep = (value: any) => {
    const cleanValue = value.replace(/\D/g, "");
    return cleanValue.replace(/^(\d{5})(\d)/, "$1-$2").slice(0, 9);
  };

  // Regras de validação por campo.
  const validationRules: Record<string, (value: string) => boolean> = {
    name: (value: string) =>
      !value || value.length < 3 || !/^[A-Za-zÀ-ÿ\s]+$/.test(value),
    professional_ssn: (value) => {
      return !isValidDocument(value);
    },
    country: (value) => !value,
    cep: (value: string) => {
      if (!value) return true;

      return !/^\d{8}$/.test(value);
    },
    state: (value) => !value,
    city: (value: string) => !value || !/^[A-Za-zÀ-ÿ\s]+$/.test(value), //Verifica se a string contém apenas letras
    neighborhood: (value) => !value || !/^[A-Za-zÀ-ÿ\s]+$/.test(value),
    street: (value) => !value || !/^[A-Za-zÀ-ÿ\s]+$/.test(value),
    number: (value) => !value || value.length <= 0,
  };

  //Função para api do CEP
  const fetchAddress = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length !== 8) {
      setErrosInput((prev) => ({
        ...prev,
        cep: true,
      }));
      return;
    }
    
    try {
      const response = await fetch(`https://opencep.com/v1/${cleanCep}`);
      if (!response.ok) {
        setErrosInput((prev) => ({
          ...prev,
          cep: true,
        }));
        return false;
      }
      const data = await response.json();

      setErrosInput((prev) => ({
        ...prev,
        cep: false,
      }));

      setUserInfo((prev: any) => ({
        ...prev,
        state: data.uf || "",
        city: data.localidade || "",
        neighborhood: data.bairro || "",
        street: data.logradouro || "",
      }));

      //Foco no número quando ser preenchidos os campos
      numeroRef.current?.focus();
      return true;
    } catch (error: any) {
      console.error("Erro ao buscar o endereço:", error);
      setErrosInput((prev) => ({
        ...prev,
        cep: true,
      }));
      return false;
    }
  };

  // Valida apenas se o campo já foi tocado
  const validateField = (field: string, value: string) => {
    // se o usuário ainda não interagiu com o campo, não valida
    if (!fieldsTouched[field]) return;

    // pega a função de validação do campo
    const validator = validationRules[field];

    // executa a validação (true = erro, false = ok)
    const error = validator ? validator(value) : false;

    // atualiza o estado de erros mantendo os anteriores
    setErrosInput((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // Marca campo como tocado e valida ao sair (onBlur)
  const handleBlur = (field: string, value: string) => {
    //- Marca o campo como "tocado"
    setFieldsTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    // - Valida o campo
    validateField(field, value);
  };

  return {
    fieldsTouched,
    errosInput,
    handleBlur,
    formatDocumentNumber,
    formatCep,
    fetchAddress,
    numeroRef,
    isInvoiceDataFilled,
    setIsInvoiceDataFilled,
  };
};
